import React from "react";
import WorkflowStepRing from "./WorkflowStepRing";
import {IProject} from "../../../models/interfaces/project.interface.ts";
import {useProject} from "../../../hooks/useProject.ts";
import {IGroupProgress} from "../../../models/interfaces/groupProgress.interface.ts";
import {IItemProgress} from "../../../models/interfaces/itemProgress.interface.ts";
import {PiWaveformThin} from "react-icons/pi";
import {FaLanguage, FaRegFileAudio} from "react-icons/fa";

import {GiConversation} from "react-icons/gi";
import {SiDatabricks} from "react-icons/si";
import {IStateEnum} from "../../../models/enums/state.enum.ts";
import {camelCaseToTitleCase} from "../../../utils/utils.ts";

type Props = {
  project: IProject;
  hoveredStep: string | null;
  setHoveredStep: (id: string | null) => void;
  onEnterLevel: (level: number) => void;
  downloadSpecific: (stepId: string, language?: string) => void;
}

const TRANSLATION_LEVEL = 1;
const AUDIO_DESCRIPTION_LEVEL = 2;

const steps = [
  {id: 'transcription', icon: PiWaveformThin},
  {id: 'subtitleTranslations', icon: FaLanguage, isMulti: true},
  {id: 'dialogueList', icon: GiConversation},
  {id: 'extendedMetadata', icon: SiDatabricks},
  {id: 'audioDescriptions', icon: FaRegFileAudio, isMulti: true},
];

const WorkflowLevel: React.FC<Props> = (
  {
    project,
    hoveredStep,
    setHoveredStep,
    onEnterLevel,
    downloadSpecific
  }: Props) => {
  const {getItemStateEnum, getGroupStateEnum, calculateGroupPercentage} = useProject();

  return (
    <div className="flex items-center justify-start gap-4">
      {steps.map((step) => {
        let groupState: IStateEnum = IStateEnum.IN_PROGRESS
        let rotation = 0;
        if (step.id === 'transcription') {
          groupState = project.transcription?.status == "fa" ? IStateEnum.FAILED : project.transcription?.status == "co" ? IStateEnum.DONE : IStateEnum.IN_PROGRESS;
          if (!project.transcription) return undefined;
          rotation = project.transcription?.status == "co" ? 360 : 0;
        } else if (step.isMulti) {
          if (project.featuresStates?.[step.id as keyof IProject['featuresStates']] === undefined) return undefined;
          const group = project.featuresStates?.[step.id as keyof IProject['featuresStates']] as IGroupProgress;
          if (Object.keys(group).length === 0) return undefined;
          rotation = ((calculateGroupPercentage(group) || 0) / 100) * 360;
          groupState = getGroupStateEnum(group);

        } else {
          if (project.featuresStates?.[step.id as keyof IProject['featuresStates']] === undefined) return undefined;
          const item = project.featuresStates?.[step.id as keyof IProject['featuresStates']] as IItemProgress;
          if (item) {
            rotation = (item.pct / 100) * 360;
            groupState = getItemStateEnum(item);
          }
        }
        return (
          <WorkflowStepRing
            key={step.id}
            id={step.id}
            rotation={rotation}
            state={groupState}
            isInteractive={Boolean(step.isMulti) || (rotation === 360 && groupState === IStateEnum.DONE)}
            icon={
              (step.id === 'subtitleTranslations' && hoveredStep === step.id) ||
              (step.id === 'audioDescriptions' && hoveredStep === step.id)
                ? 'arrow-next'
                : step.icon
            }
            isHovered={hoveredStep === step.id}
            onEnter={() => setHoveredStep(step.id)}
            onLeave={() => setHoveredStep(null)}
            onClick={() => {
              if (step.isMulti) {
                onEnterLevel(step.id === 'subtitleTranslations' ? TRANSLATION_LEVEL : AUDIO_DESCRIPTION_LEVEL);
              } else if (rotation === 360) {
                if (step.id === "transcription") {
                  downloadSpecific(step.id, project.sourceLanguage);
                } else {
                  downloadSpecific(step.id);
                }
              }
            }}
            tooltip={camelCaseToTitleCase(step.id)}
          />
        );
      })}
    </div>
  );
};

export default WorkflowLevel;
